// frontend/src/hooks/useBreakpointBoundRef.js
import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Dual-header shells (Admin / Asset) keep both the mobile (`lg:hidden`) and
 * desktop (`hidden lg:flex`) chrome mounted. Click-outside and viewport
 * anchors must talk to the *visible* node — not whichever ref callback ran last.
 *
 * If the shared ref stays on the hidden desktop wrapper, a tap on Sign out
 * (or any menu row) is treated as outside. `useClickOutside` then unmounts
 * the menu on mousedown/touchstart and the row `onClick` never fires.
 *
 * @param {boolean} isLg - Tailwind `lg` (`min-width: 1024px`)
 * @param {React.RefObject<HTMLElement|null>} sharedRef - typically from `useClickOutside`
 * @returns {{
 *   bindMobile: (el: HTMLElement|null) => void,
 *   bindDesktop: (el: HTMLElement|null) => void,
 *   visibleRef: { current: HTMLElement|null },
 * }}
 */
export function useBreakpointBoundRef(isLg, sharedRef) {
  const mobileRef = useRef(null);
  const desktopRef = useRef(null);

  const sync = useCallback(() => {
    if (!sharedRef) return;
    sharedRef.current = isLg ? desktopRef.current : mobileRef.current;
  }, [isLg, sharedRef]);

  const bindMobile = useCallback(
    (el) => {
      mobileRef.current = el;
      sync();
    },
    [sync]
  );

  const bindDesktop = useCallback(
    (el) => {
      desktopRef.current = el;
      sync();
    },
    [sync]
  );

  useEffect(() => {
    sync();
  }, [sync]);

  const visibleRef = useMemo(
    () => ({
      get current() {
        return isLg ? desktopRef.current : mobileRef.current;
      },
    }),
    [isLg]
  );

  return { bindMobile, bindDesktop, visibleRef };
}

export default useBreakpointBoundRef;
